/**
 * Usage Limits Store
 * 
 * Handles usage limits and upgrade flows for ANY plan (not just "freemium").
 * 
 * Any plan can have limits:
 * - Free plan: $0.50 AI budget (lifetime)
 * - Basic plan ($14): $15/month AI budget
 * - Standard plan ($36): $45/month AI budget
 * - Premium plan ($100): $140/month AI budget
 * - Business plan: Team usage limits
 * - etc.
 */

import { create } from 'zustand';
import { createFileLogger } from '../utils/logger';
import { useThinkingLoaderStore } from '../components/Space/SpaceChat/store/thinkingLoaderStore';

const log = createFileLogger('USAGE_LIMITS_STORE');

export interface UsageLimitInfo {
  limitType:
    | 'monthly_cost'
    | 'monthlySpend'
    | 'teamUsage'
    | 'imageGenerations'
    | 'projects';
  currentUsage: {
    used: number;
    max: number;
    unit: string; // 'credits', 'dollars', etc.
  };
  planName: string; // 'free', 'plus', 'business', etc.
  message: string;
  title?: string; // Optional custom title
  description?: string; // Optional custom description
  /** When true, all plans above current show an upgrade button (for general upgrade CTAs).
   *  When false/unset, only the immediately next plan shows a button (for limit/feature gates). */
  showAllPlans?: boolean;
}

export interface PendingRequest {
  id: string;
  type: 'inline_ai' | 'chat' | 'stream';
  description: string;
  retryFunction: () => Promise<any>;
  context: any;
  resolve?: (result: any) => void;
  reject?: (error: any) => void;
}

interface UpgradeModalState {
  isOpen: boolean;
  limitInfo?: UsageLimitInfo;
  pendingRequest?: PendingRequest;
  upgradeInProgress: boolean;
  selectedTier?: 'basic' | 'standard' | 'premium';  // Selected plan tier for upgrade
}

interface UsageLimitsStore {
  modal: UpgradeModalState;
  
  // Show upgrade modal with optional request continuation
  showUpgradeModal: (limitInfo: UsageLimitInfo, pendingRequest?: PendingRequest) => void;
  
  // Hide upgrade modal
  hideUpgradeModal: () => void;
  
  // Complete successful upgrade (closes modal without rejecting)
  completeUpgrade: () => void;
  
  // Start upgrade process (sets upgradeInProgress flag)
  startUpgradeProcess: () => void;
  
  // Continue pending request after successful upgrade
  continuePendingRequest: () => Promise<any>;
  
  // Set selected tier
  setSelectedTier: (tier: 'basic' | 'standard' | 'premium') => void;
  
  // Handle upgrade completion
  handleUpgrade: () => void;
  
  // Handle upgrade cancellation
  handleCancel: () => void;
  
  // Check if upgrade is in progress
  isUpgrading: () => boolean;
}

const useUsageLimitsStore = create<UsageLimitsStore>((set, get) => ({
  modal: {
    isOpen: false,
    upgradeInProgress: false,
  },

  showUpgradeModal: (limitInfo: UsageLimitInfo, pendingRequest?: PendingRequest) => {
    const { modal } = get();
    
    // Prevent showing multiple modals
    if (modal.isOpen) {
      log.info('[UsageLimitsStore] Modal already open, ignoring duplicate request');
      return;
    }
    
    log.info('[UsageLimitsStore] Showing upgrade modal:', {
      limitType: limitInfo.limitType,
      planName: limitInfo.planName,
      hasPendingRequest: !!pendingRequest
    });
    
    set({
      modal: {
        isOpen: true,
        limitInfo,
        pendingRequest,
        upgradeInProgress: false,
      }
    });
  },

  hideUpgradeModal: () => {
    const { modal } = get();
    
    // If there's a pending request and we're not upgrading, reject it
    if (modal.pendingRequest && !modal.upgradeInProgress && modal.pendingRequest.reject) {
      modal.pendingRequest.reject(new Error('User cancelled upgrade'));
    }
    
    set({
      modal: {
        isOpen: false,
        upgradeInProgress: false,
      }
    });
  },

  completeUpgrade: () => {
    const { modal } = get();
    
    log.info('[UsageLimitsStore] Upgrade completed successfully');
    
    // Close modal without rejecting pending request (upgrade was successful)
    set({
      modal: {
        isOpen: false,
        upgradeInProgress: false,
      }
    });
  },

  startUpgradeProcess: () => {
    const { modal } = get();
    
    log.info('[UsageLimitsStore] Starting upgrade process (inline payment)');
    
    set({
      modal: {
        ...modal,
        upgradeInProgress: true,
      }
    });
  },

  setSelectedTier: (tier: 'basic' | 'standard' | 'premium') => {
    const { modal } = get();
    
    log.info(`[UsageLimitsStore] Setting selected tier: ${tier}`);
    
    set({
      modal: {
        ...modal,
        selectedTier: tier,
      }
    });
  },

  // 🔥 Continue pending request after successful upgrade
  continuePendingRequest: async () => {
    const { modal } = get();
    if (modal.pendingRequest?.retryFunction) {
      try {
        console.log('[UsageLimitsStore] Continuing pending request:', modal.pendingRequest.description);
        const result = await modal.pendingRequest.retryFunction();
        
        // Resolve the original promise
        if (modal.pendingRequest.resolve) {
          modal.pendingRequest.resolve(result);
        }
        
        // Clear the modal
        set({
          modal: {
            isOpen: false,
            upgradeInProgress: false,
          }
        });
        
        return result;
      } catch (error) {
        console.error('[UsageLimitsStore] Error continuing request:', error);
        
        // Reject the original promise
        if (modal.pendingRequest.reject) {
          modal.pendingRequest.reject(error);
        }
        
        // Clear the modal
        set({
          modal: {
            isOpen: false,
            upgradeInProgress: false,
          }
        });
        
        throw error;
      }
    }
  },

  handleUpgrade: async () => {
    const { modal } = get();
    
    log.info('[UsageLimitsStore] handleUpgrade called - DEPRECATED');
    log.warn('[UsageLimitsStore] This method is deprecated. UpgradeModal now handles payment inline using multi-step flow.');
    
    // DEPRECATED: This method previously redirected to /signup
    // Now the UpgradeModal uses a multi-step flow:
    // Step 1: Select plan (existing tier cards)
    // Step 2: Enter payment (inline Stripe form)
    // 
    // Buttons in UpgradeModal and SubscriptionSection now call:
    // - setStoreSelectedTier() to set the selected plan
    // - setStep('enter-payment') to show the payment form
    // 
    // Keeping this method for backward compatibility but it does nothing.
    
    set({
      modal: {
        ...modal,
        upgradeInProgress: false,
      }
    });
  },

  handleCancel: () => {
    const { modal } = get();
    
    log.info('[UsageLimitsStore] Upgrade cancelled by user');
    
    if (modal.pendingRequest && modal.pendingRequest.reject) {
      modal.pendingRequest.reject(new Error('User cancelled upgrade'));
    }
    
    get().hideUpgradeModal();
  },

  isUpgrading: () => {
    return get().modal.upgradeInProgress;
  },
}));

export default useUsageLimitsStore;

// Convenience function for intercepting LLM requests
export const interceptLLMRequest = async function<T>(
  requestType: 'inline_ai' | 'chat' | 'stream',
  requestFunction: () => Promise<T>,
  context: {
    userPrompt?: string;
    selectedText?: string;
    operation?: string;
  }
): Promise<T> {
  const store = useUsageLimitsStore.getState();
  
  try {
    // Try the original request
    return await requestFunction();
    
  } catch (error: any) {
    // Debug: Log the complete error structure
    console.log('🔍 [INTERCEPT_DEBUG] Caught error:', error);
    console.log('🔍 [INTERCEPT_DEBUG] Error details:', {
      status: error.status,
      message: error.message,
      data: error.data,
      dataType: typeof error.data,
      dataKeys: error.data ? Object.keys(error.data) : 'no data',
      hasUpgradeRequired: !!error.data?.upgrade_required,
      errorString: error.data?.error,
      errorType: typeof error.data?.error,
      errorIncludesLimit: error.data?.error?.includes?.('LIMIT_EXCEEDED'),
      fullErrorData: JSON.stringify(error.data, null, 2)
    });
    
    // Check if this is a limit exceeded error - handle FastAPI detail wrapping.
    // 402 Payment Required is a billing limit; 403 with upgrade signals are also billing.
    const isLimitError = error.status === 402 || (error.status === 403 && (
      error.data?.upgrade_required === true ||
      error.data?.detail?.upgrade_required === true ||
      error.data?.error?.includes?.('LIMIT_EXCEEDED') ||
      error.data?.detail?.error?.includes?.('LIMIT_EXCEEDED') ||
      error.data?.error?.includes?.('COST_LIMIT_EXCEEDED') ||
      error.data?.detail?.error?.includes?.('COST_LIMIT_EXCEEDED') ||
      error.data?.error === 'MONTHLY_COST_LIMIT_EXCEEDED' ||
      error.data?.detail?.error === 'MONTHLY_COST_LIMIT_EXCEEDED' ||
      error.data?.error === 'MEMBER_BUDGET_EXCEEDED' ||
      error.data?.detail?.error === 'MEMBER_BUDGET_EXCEEDED' ||
      error.message?.includes?.('limit') ||
      error.message?.includes?.('upgrade')
    ));
    
    console.log('🔍 [INTERCEPT_DEBUG] Detailed error analysis:', {
      status: error.status,
      hasData: !!error.data,
      dataKeys: error.data ? Object.keys(error.data) : [],
      errorField: error.data?.error,
      detailErrorField: error.data?.detail?.error,
      upgradeRequired: error.data?.upgrade_required,
      detailUpgradeRequired: error.data?.detail?.upgrade_required,
      isLimitError
    });
    
    console.log('🔍 [INTERCEPT_DEBUG] Is limit error?', isLimitError);
    
    // 🚨 CHECK FOR TOKEN LIMIT ERROR (not a 403, but validation error)
    if (error.code === 'TOKEN_LIMIT_EXCEEDED') {
      console.log('🔍 [INTERCEPT_DEBUG] Token limit exceeded - letting error bubble up for toast');
      // Don't intercept - let it bubble to useMessageSending for toast
      throw error;
    }
    
    if (isLimitError) {
      // 🛑 STEP 1: Stop the thinking loader IMMEDIATELY
      console.log('🛑 [INTERCEPT] Stopping thinking loader...');
      useThinkingLoaderStore.getState().stopWaiting();
      console.log('✅ [INTERCEPT] Thinking loader stopped');
      
      // ✨ STEP 2: Check promo eligibility when limit hit
      console.log('[INTERCEPT] Checking promo eligibility...');
      try {
        const { default: usePromoStore } = await import('../stores/promoStore');
        await usePromoStore.getState().fetchStatus();
        console.log('[INTERCEPT] ✅ Promo status checked');
      } catch (promoError) {
        console.error('[INTERCEPT] Failed to check promo status (non-fatal):', promoError);
        // Continue anyway - promo is optional
      }
      
      // Create retry function
      const retryFunction = async () => {
        log.info('[InterceptLLM] Retrying request after upgrade');
        return await requestFunction();
      };
      
      // Create pending request
      const pendingRequest: PendingRequest = {
        id: `${requestType}_${Date.now()}`,
        type: requestType,
        description: context.userPrompt || context.operation || 'AI request',
        retryFunction,
        context
      };
      
      // Create limit info from error - handle FastAPI detail wrapping
      const errorDetail = error.data?.detail || error.data;
      const errorCode = errorDetail?.error;
      const errorReason = errorDetail?.reason;

      // Special case: team subscription is inactive (owner cancelled).
      // Show an informational notice rather than an upgrade prompt.
      if (errorCode === 'MEMBER_BUDGET_EXCEEDED' && errorReason === 'team_subscription_inactive') {
        const limitInfo: UsageLimitInfo = {
          limitType: 'monthly_cost',
          currentUsage: { used: 0, max: 0, unit: 'dollars' },
          planName: errorDetail?.billing_plan || 'free',
          message: 'Your team\'s subscription is inactive.',
          title: 'Team subscription inactive',
          description: 'Your team\'s subscription is currently inactive. Ask the team owner to reactivate the subscription to continue using AI.',
          showAllPlans: false,
        };
        await new Promise(resolve => setTimeout(resolve, 500));
        return new Promise((resolve, reject) => {
          pendingRequest.resolve = resolve;
          pendingRequest.reject = reject;
          store.showUpgradeModal(limitInfo, pendingRequest);
        });
      }

      // Per-user budget exceeded: fields are monthly_spend_usd / monthly_budget_usd (dollars).
      // Legacy/org budget: fields are limits.monthly_cost.used/max (also dollars).
      let usedCents: number;
      let maxCents: number;
      if (errorCode === 'MEMBER_BUDGET_EXCEEDED') {
        usedCents = Math.round((errorDetail?.monthly_spend_usd ?? 0) * 100);
        maxCents  = Math.round((errorDetail?.monthly_budget_usd ?? 0) * 100);
      } else {
        const usedValue = errorDetail?.limits?.monthly_cost?.used || errorDetail?.limits?.used || 0;
        const maxValue  = errorDetail?.limits?.monthly_cost?.max  || errorDetail?.limits?.max  || 1000;
        const isMonthlyCredit = errorDetail?.limit_type === 'monthly_cost';
        usedCents = isMonthlyCredit ? Math.round(usedValue * 100) : usedValue;
        maxCents  = isMonthlyCredit ? Math.round(maxValue  * 100) : maxValue;
      }

      const limitInfo: UsageLimitInfo = {
        limitType: errorDetail?.limit_type || 'monthly_cost',
        currentUsage: {
          used: usedCents,
          max:  maxCents,
          unit: 'dollars'
        },
        planName: errorDetail?.plan_type || errorDetail?.billing_plan || 'free',
        message: errorDetail?.message || 'You have reached your usage limit.'
      };
      
      // 🛑 STEP 3: Wait 500ms before showing modal (let user see any error messages first)
      await new Promise(resolve => setTimeout(resolve, 500));
      console.log('✅ [INTERCEPT] 500ms delay complete, showing modal...');
      
      // Return a promise that resolves when the upgrade flow completes
      return new Promise((resolve, reject) => {
        pendingRequest.resolve = resolve;
        pendingRequest.reject = reject;
        
        // Show upgrade modal with continuation
        store.showUpgradeModal(limitInfo, pendingRequest);
      });
    } else {
      // Not a limit error, re-throw
      throw error;
    }
  }
}
